import React, { FC, useCallback, useEffect } from 'react';
import styles from './Toolbar.module.scss';
import classNames from 'classnames';
import { AppDispatch } from '../../../../../store';
import { fullScreenModeChanged, undo, redo } from '../../annotation.slice';
import { RootState } from '../../../../../rootReducer';
import { connect } from 'react-redux';
import { Tooltip } from 'antd';
import { WarningOutlined, WarningFilled } from '@ant-design/icons';
import { generatePickUnselected, generatePolygon, generateRectangle } from '../../../Canvas/canvas.slice';
import { DataQCAssessment } from '../../Tree/QC/qc.slice';
import { useKeyPress } from '../../../../Shared/UseKeyPress';

type Props = {
  isDisabled: boolean;
  imageDataQCAssessment: DataQCAssessment | null;
  showQCOption: boolean;
  showImageEffects: boolean;
  setShowImageEffects: (value: boolean) => void;
  onQC: () => void;
};

type StoreProps = {
  polygonMode: boolean;
  rectangleMode: boolean;
  pickUnselect: boolean;
  fullScreenMode: boolean;
  generatePolygon: (mode: boolean) => void;
  generatePickUnselected: (mode: boolean) => void;
  generateRectangle: (mode: boolean) => void;
  fullScreenModeChanged: (mode: boolean) => void;
  onUndo: () => void;
  onRedo: () => void;
};

const Toolbar: FC<Props & StoreProps> = ({
  isDisabled,
  polygonMode,
  rectangleMode,
  pickUnselect,
  fullScreenMode,
  imageDataQCAssessment,
  showQCOption,
  showImageEffects,
  setShowImageEffects,
  generatePolygon,
  generatePickUnselected,
  generateRectangle,
  fullScreenModeChanged,
  onRedo,
  onUndo,
  onQC
}) => {
  const toggleFullscreen = useCallback(() => {
    fullScreenModeChanged(!fullScreenMode);
  }, [fullScreenMode, fullScreenModeChanged]);

  useKeyPress('F11', true, toggleFullscreen);

  const toggleImageSettings = useCallback(() => {
    setShowImageEffects(!showImageEffects);
  }, [setShowImageEffects, showImageEffects]);

  useKeyPress('i', true, toggleImageSettings);

  const fullScreenIcon = fullScreenMode ? styles.fullScreenClose : styles.fullScreen;
  const fullScreenText = fullScreenMode ? 'Close Fullscreen' : 'Fullscreen';

  return (
    <div className={!isDisabled ? '' : 'isDisabledToolbar'}>
      <div className={styles.toolbarContainer}>
        <div className={styles.toolbar}>
          <Tooltip placement='left' title={fullScreenText}>
            <div className={styles.toolbarItem}>
              <img
                className={classNames(fullScreenIcon)}
                onClick={() => {
                  fullScreenModeChanged(!fullScreenMode);
                }}
                alt='Fullscreen'
              />
            </div>
          </Tooltip>

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Selection'>
            <div className={styles.toolbarItem}>
              <img
                className={classNames(styles.pick, {
                  [styles.selected]: pickUnselect
                })}
                onClick={() => {
                  generatePickUnselected(!pickUnselect);
                }}
                alt='Selection'
              />
            </div>
          </Tooltip>

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Draw Polygon'>
            <div className={styles.toolbarItem}>
              <img
                className={classNames(styles.poligon, {
                  [styles.selected]: polygonMode
                })}
                onClick={() => {
                  generatePolygon(!polygonMode);
                }}
                alt='Draw Polygon'
              />
            </div>
          </Tooltip>

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Draw Rectangle'>
            <div className={styles.toolbarItem}>
              <img
                className={classNames(styles.square, {
                  [styles.selected]: rectangleMode
                })}
                onClick={() => {
                  generateRectangle(!rectangleMode);
                }}
                alt='Draw Rectangle'
              />
            </div>
          </Tooltip>

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Image Settings'>
            <div className={styles.toolbarItem}>
              <img
                className={classNames(styles.settingsItem, {
                  [styles.selected]: showImageEffects
                })}
                onClick={() => {
                  setShowImageEffects(!showImageEffects);
                }}
                alt='Image Settings'
              />
            </div>
          </Tooltip>

          {showQCOption && (
            <Tooltip placement='left' title='QC'>
              <div className={styles.toolbarItem} style={{ marginTop: '1rem' }}>
                <div
                  className={classNames(styles.qcItem, {
                    [styles.selected]: imageDataQCAssessment && imageDataQCAssessment.haveQC
                  })}
                  onClick={onQC}
                >
                  {imageDataQCAssessment && imageDataQCAssessment.haveQC ? (
                    <WarningFilled className={styles.warningIcon} />
                  ) : (
                    <WarningOutlined className={styles.warningIcon} />
                  )}
                </div>
              </div>
            </Tooltip>
          )}

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Undo'>
            <div className={styles.toolbarItem}>
              <img className={classNames(styles.undoItem)} onClick={onUndo} alt='Undo' />
            </div>
          </Tooltip>

          <div className={styles.delimeter} />

          <Tooltip placement='left' title='Redo'>
            <div className={styles.toolbarItem}>
              <img className={classNames(styles.redoItem)} onClick={onRedo} alt='Redo' />
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  polygonMode: state.canvas.polygonMode,
  rectangleMode: state.canvas.rectangleMode,
  pickUnselect: state.canvas.pickUnselect,
  fullScreenMode: state.annotation.fullScreenMode
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  generatePolygon: (mode: boolean) => dispatch(generatePolygon(mode)),
  generatePickUnselected: (mode: boolean) => dispatch(generatePickUnselected(mode)),
  generateRectangle: (mode: boolean) => dispatch(generateRectangle(mode)),
  fullScreenModeChanged: (mode: boolean) => dispatch(fullScreenModeChanged(mode)),
  onUndo: () => dispatch(undo()),
  onRedo: () => dispatch(redo())
});

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar);
